import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Camera,
  ImageIcon,
  FileUp,
  Loader2,
  Plus,
  Trash2,
  ChevronRight,
  ChevronLeft,
  Check,
  Scale,
  AlertTriangle,
  Pencil,
} from "lucide-react";
import { Capacitor } from "@capacitor/core";
import {
  bucketIntoTiers,
  formatWeight,
  priceForUnit,
} from "@shared/weight";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { useToast } from "@/hooks/use-toast";

const CATEGORIES = [
  { value: "produce", label: "Produce" },
  { value: "eggs", label: "Eggs & Dairy" },
  { value: "baked", label: "Baked Goods" },
  { value: "prepared", label: "Prepared Foods" },
  { value: "meat", label: "Meat & Poultry" },
  { value: "pantry", label: "Pantry" },
  { value: "flowers", label: "Flowers" },
  { value: "homemade", label: "Homemade / Crafted" },
  { value: "garden", label: "Garden" },
  { value: "other", label: "Other" },
];

const LS_RATE = "weightBatch.pricePerLb";
const LS_PRODUCT = "weightBatch.lastProduct";

function tierLabelsFor(count: number): string[] {
  switch (count) {
    case 1: return ["Standard"];
    case 2: return ["Small", "Large"];
    case 3: return ["Small", "Medium", "Large"];
    case 4: return ["Small", "Medium", "Large", "Extra Large"];
    case 5: return ["Extra Small", "Small", "Medium", "Large", "Extra Large"];
    default: return Array.from({ length: count }, (_, i) => `Tier ${i + 1}`);
  }
}

type Props = {
  onComplete: (created?: any) => void;
  onCancel: () => void;
  initial?: {
    name?: string;
    category?: string;
    description?: string;
    imageUrls?: string[];
    pricePerLbCents?: number;
  };
};

type Row = {
  id: string;
  weight: string;
  priceOverride: string | null;
  flag: string | null;
};

const rowId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `r${Date.now()}${Math.round(Math.random() * 1e6)}`;

const newRow = (): Row => ({ id: rowId(), weight: "", priceOverride: null, flag: null });

const toCentiLb = (lb: string): number | null => {
  const n = Number(lb);
  if (!lb.trim() || !Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
};

const dollars = (cents: number) => (cents / 100).toFixed(2);

// A weight is flagged when it sits far from the batch median — the classic OCR
// slip (45.1 among a flock of ~4s). Median-relative so it scales with product.
function flagWeights(weightsCentiLb: (number | null)[]): (string | null)[] {
  const present = weightsCentiLb.filter((w): w is number => w != null);
  if (present.length < 3) return weightsCentiLb.map(() => null);
  const sorted = [...present].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  if (median <= 0) return weightsCentiLb.map(() => null);
  return weightsCentiLb.map((w) => {
    if (w == null) return null;
    const ratio = w / median;
    if (ratio >= 3 || ratio <= 1 / 3) return "Way off from the rest — double check this weight";
    return null;
  });
}

export function WeightBatchGrid({ onComplete, onCancel, initial }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [phase, setPhase] = useState<"basics" | "fill" | "grid" | "tiers">(
    initial ? "fill" : "basics",
  );

  const [name, setName] = useState(initial?.name ?? "");
  const [category, setCategory] = useState(initial?.category || "meat");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [rateInput, setRateInput] = useState(
    initial?.pricePerLbCents ? (initial.pricePerLbCents / 100).toString() : "",
  );
  const [imageUrls, setImageUrls] = useState<string[]>(initial?.imageUrls ?? []);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [tierImages, setTierImages] = useState<Record<string, string>>({});
  const [uploadingTier, setUploadingTier] = useState<string | null>(null);
  const [bulkDiscountOn, setBulkDiscountOn] = useState(false);
  const [discountThreshold, setDiscountThreshold] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");

  const [rows, setRows] = useState<Row[]>([newRow()]);
  const [extracting, setExtracting] = useState(false);

  const [tierCount, setTierCount] = useState(3);
  const [cutPoints, setCutPoints] = useState<number[]>([]);

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tierPhotoInputRef = useRef<HTMLInputElement>(null);
  const tierCameraInputRef = useRef<HTMLInputElement>(null);
  const pendingTierRef = useRef<string | null>(null);

  useEffect(() => {
    if (initial) return;
    const savedRate = localStorage.getItem(LS_RATE);
    if (savedRate) setRateInput(savedRate);
    const savedProduct = localStorage.getItem(LS_PRODUCT);
    if (savedProduct) {
      try {
        const p = JSON.parse(savedProduct);
        if (p.name) setName(p.name);
        if (p.category) setCategory(p.category);
        if (p.description) setDescription(p.description);
      } catch {}
    }
  }, []);

  const pricePerLbCents = useMemo(() => {
    const n = Number(rateInput);
    return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
  }, [rateInput]);

  const centiWeights = useMemo(() => rows.map((r) => toCentiLb(r.weight)), [rows]);
  const flags = useMemo(() => flagWeights(centiWeights), [centiWeights]);

  const priceCentsForRow = (row: Row, centi: number | null): number => {
    if (row.priceOverride != null) {
      const n = Number(row.priceOverride);
      return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : 0;
    }
    if (centi == null || pricePerLbCents <= 0) return 0;
    return priceForUnit(centi, pricePerLbCents);
  };

  const totals = useMemo(() => {
    let count = 0;
    let totalCenti = 0;
    let totalCents = 0;
    rows.forEach((row, i) => {
      const centi = centiWeights[i];
      if (centi == null) return;
      count += 1;
      totalCenti += centi;
      totalCents += priceCentsForRow(row, centi);
    });
    return { count, totalCenti, totalCents };
  }, [rows, centiWeights, pricePerLbCents]);

  const validCentiWeights = useMemo(
    () => centiWeights.filter((w): w is number => w != null),
    [centiWeights],
  );

  const uploadFile = async (file: File): Promise<string | null> => {
    const formData = new FormData();
    formData.append("image", file, file.name || "batch.jpg");
    const res = await fetch("/api/uploads/item-image", {
      method: "POST",
      credentials: "include",
      body: formData,
    });
    if (!res.ok) return null;
    return (await res.json()).imageUrl || null;
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const mergeExtracted = (weights: number[], warnings: { index: number; reason: string }[]) => {
    if (weights.length === 0) {
      toast({ title: "No weights found", description: "Try a clearer photo, or type them in." });
      return;
    }
    const warnByIndex = new Map(warnings.map((w) => [w.index, w.reason]));
    const extractedRows: Row[] = weights.map((centi, i) => ({
      id: rowId(),
      weight: (centi / 100).toFixed(2),
      priceOverride: null,
      flag: warnByIndex.get(i) ?? null,
    }));
    setRows((prev) => {
      const kept = prev.filter((r) => r.weight.trim());
      return [...kept, ...extractedRows];
    });
    setPhase("grid");
  };

  const extractImage = async (file: File) => {
    setExtracting(true);
    try {
      const imageBase64 = await fileToBase64(file);
      const res = await fetch("/api/seller/weight-batch/extract", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageBase64 }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      mergeExtracted(data.weights || [], data.warnings || []);
    } catch {
      toast({
        title: "Couldn't read that photo",
        description: "Add the weights by hand below.",
        variant: "destructive",
      });
      setPhase("grid");
    } finally {
      setExtracting(false);
    }
  };

  // CSV / plain text: pull every number out of the text. Handles one-per-line,
  // comma-separated, and "Bird 1: 4.5 lb" style rows.
  const parseTextWeights = (text: string): number[] => {
    const nums: number[] = [];
    for (const line of text.split(/\r?\n/)) {
      const matches = line.match(/-?\d+(?:\.\d+)?/g);
      if (!matches) continue;
      // Skip a leading integer index like "1," or "12." then take the weight.
      const values = matches.map(Number).filter((n) => Number.isFinite(n) && n > 0);
      if (values.length === 0) continue;
      const weight = values.length > 1 ? values[values.length - 1] : values[0];
      if (weight > 0 && weight < 1000) nums.push(Math.round(weight * 100));
    }
    return nums;
  };

  const handleFileUpload = async (file: File) => {
    const isTexty =
      file.type.startsWith("text/") ||
      /\.(csv|txt|tsv)$/i.test(file.name);
    if (isTexty) {
      const text = await file.text();
      const weights = parseTextWeights(text);
      mergeExtracted(weights, []);
      return;
    }
    const isSpreadsheet = /\.(xlsx|xls)$/i.test(file.name);
    if (isSpreadsheet) {
      setExtracting(true);
      try {
        const xlsxBase64 = await fileToBase64(file);
        const res = await fetch("/api/seller/weight-batch/extract", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ xlsxBase64 }),
        });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        mergeExtracted(data.weights || [], data.warnings || []);
      } catch {
        toast({
          title: "Couldn't read that spreadsheet",
          description: "Use a .xlsx, .csv, or type them in.",
          variant: "destructive",
        });
        setPhase("grid");
      } finally {
        setExtracting(false);
      }
      return;
    }
    setExtracting(true);
    try {
      const imageBase64 = await fileToBase64(file);
      const res = await fetch("/api/seller/weight-batch/extract", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageBase64 }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      mergeExtracted(data.weights || [], data.warnings || []);
    } catch {
      toast({
        title: "Couldn't parse that file",
        description: "Use a CSV or text list of weights, or type them in.",
        variant: "destructive",
      });
      setPhase("grid");
    } finally {
      setExtracting(false);
    }
  };

  const addCoverPhoto = async (file: File) => {
    setUploadingPhoto(true);
    try {
      const url = await uploadFile(file);
      if (url) setImageUrls((prev) => [...prev, url].slice(0, 6));
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Optional per-size example photo. Fires the shared hidden input synchronously
  // (trap #11) after tagging which tier it is for.
  const pickTierPhoto = (tier: string, source: "camera" | "gallery") => {
    pendingTierRef.current = tier;
    (source === "camera" ? tierCameraInputRef : tierPhotoInputRef).current?.click();
  };
  const onTierPhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const tier = pendingTierRef.current;
    pendingTierRef.current = null;
    if (!file || !tier) return;
    setUploadingTier(tier);
    try {
      const url = await uploadFile(file);
      if (url) setTierImages((prev) => ({ ...prev, [tier]: url }));
    } finally {
      setUploadingTier(null);
    }
  };

  const updateRow = (id: string, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id: string) =>
    setRows((prev) => (prev.length === 1 ? [newRow()] : prev.filter((r) => r.id !== id)));
  const addRow = () => setRows((prev) => [...prev, newRow()]);

  const goToTiers = () => {
    if (pricePerLbCents <= 0) {
      toast({ title: "Set a price per lb", description: "Enter the rate at the top of the grid." });
      return;
    }
    if (validCentiWeights.length < 1) {
      toast({ title: "Add at least one weight", variant: "destructive" });
      return;
    }
    const count = Math.min(tierCount, validCentiWeights.length);
    setTierCount(count);
    const buckets = bucketIntoTiers(validCentiWeights, count);
    // Derive editable cut points (upper bound of each non-last bucket) from the
    // proposed split so the seller adjusts boundaries, not raw indices.
    const cuts = buckets.slice(0, -1).map((b) => b.max);
    setCutPoints(cuts);
    setPhase("tiers");
  };

  const sortedWeights = useMemo(
    () => [...validCentiWeights].sort((a, b) => a - b),
    [validCentiWeights],
  );
  const weightFloor = sortedWeights[0] ?? 0;
  const weightCeil = sortedWeights[sortedWeights.length - 1] ?? 0;

  // Assign each unit to a tier index by the current cut points, then summarize.
  const tierPreview = useMemo(() => {
    const labels = tierLabelsFor(tierCount);
    const buckets = Array.from({ length: tierCount }, () => [] as number[]);
    for (const centi of validCentiWeights) {
      let idx = cutPoints.findIndex((c) => centi <= c);
      if (idx === -1) idx = tierCount - 1;
      buckets[idx].push(centi);
    }
    return buckets.map((weights, i) => {
      const sorted = [...weights].sort((a, b) => a - b);
      const min = sorted[0] ?? null;
      const max = sorted[sorted.length - 1] ?? null;
      const prices = weights.map((w) => priceForUnit(w, pricePerLbCents));
      return {
        label: labels[i],
        tier: labels[i].toLowerCase(),
        count: weights.length,
        min,
        max,
        priceMin: prices.length ? Math.min(...prices) : null,
        priceMax: prices.length ? Math.max(...prices) : null,
      };
    });
  }, [validCentiWeights, cutPoints, tierCount, pricePerLbCents]);

  const publish = useMutation({
    mutationFn: async () => {
      const labels = tierLabelsFor(tierCount);
      const units: { weightCentiLb: number; tier: string }[] = [];
      for (const centi of validCentiWeights) {
        let idx = cutPoints.findIndex((c) => centi <= c);
        if (idx === -1) idx = tierCount - 1;
        units.push({ weightCentiLb: centi, tier: labels[idx].toLowerCase() });
      }
      const res = await fetch("/api/seller/weight-batch", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productGroup: {
            name,
            description,
            category,
            imageUrls,
            pricePerLbCents,
            bulkDiscountThresholdCents:
              bulkDiscountOn && Number(discountThreshold) > 0 && Number(discountPercent) > 0
                ? Math.round(Number(discountThreshold) * 100)
                : null,
            bulkDiscountPercent:
              bulkDiscountOn && Number(discountThreshold) > 0 && Number(discountPercent) > 0
                ? Math.round(Number(discountPercent))
                : null,
          },
          tiers: labels.map((label) => ({
            tier: label.toLowerCase(),
            label,
            imageUrl: tierImages[label.toLowerCase()] ?? null,
          })),
          units,
        }),
      });
      if (!res.ok) {
        const raw = await res.text();
        let parsed: { message?: string } | null = null;
        try {
          parsed = JSON.parse(raw);
        } catch {}
        throw new Error(parsed?.message || raw || "Could not publish batch");
      }
      return res.json();
    },
    onSuccess: (created) => {
      localStorage.setItem(LS_RATE, rateInput);
      localStorage.setItem(LS_PRODUCT, JSON.stringify({ name, category, description }));
      queryClient.invalidateQueries({ queryKey: ["/api/seller/items"] });
      queryClient.invalidateQueries({ queryKey: ["/api/items"] });
      toast({
        title: "Batch listed!",
        description: `${totals.count} units across ${tierCount} sizes are live.`,
      });
      onComplete(created);
    },
    onError: (e: any) => {
      toast({
        title: "Couldn't publish",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const isNativeCapture = Capacitor.isNativePlatform();

  if (phase === "basics") {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="space-y-4 pt-6">
          <div className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">List by weight (batch)</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            List many individually-weighed units at once. We'll group them into sizes for buyers.
          </p>

          <div className="space-y-1.5">
            <Label>Product name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Whole Pasture-Raised Chicken"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Category</Label>
            <div className="flex flex-wrap gap-1">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategory(cat.value)}
                  className={`rounded-full px-2 py-0.5 text-[11px] font-medium transition ${
                    category === cat.value
                      ? "bg-primary text-primary-foreground"
                      : "border border-border hover:border-primary/50"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[70px]"
              placeholder="How they're raised, what buyers get."
            />
          </div>

          <div className="space-y-1.5">
            <Label>Price per lb ($)</Label>
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={rateInput}
              onChange={(e) => setRateInput(e.target.value)}
              placeholder="Type price per lb"
            />
            <p className="text-[11px] text-muted-foreground">
              Each unit's price = its weight × this rate. You can override any single price later.
            </p>
          </div>

          <div className="space-y-2 rounded-lg border p-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={bulkDiscountOn}
                onChange={(e) => setBulkDiscountOn(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-xs font-semibold text-foreground">Add a bulk discount</span>
            </label>
            {bulkDiscountOn && (
              <div className="space-y-2">
                <p className="text-[10px] text-muted-foreground">
                  Once a buyer's order from you reaches the amount below, they get the % off your
                  items. The discount comes out of your earnings.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-[10px]">Spend at least ($)</Label>
                    <Input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step="0.01"
                      value={discountThreshold}
                      onChange={(e) => setDiscountThreshold(e.target.value)}
                      placeholder="e.g. 50"
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px]">Discount (%)</Label>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={90}
                      value={discountPercent}
                      onChange={(e) => setDiscountPercent(e.target.value)}
                      placeholder="e.g. 10"
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Photos</Label>
            <div className="flex flex-wrap gap-2">
              {imageUrls.map((url, i) => (
                <div key={url} className="relative h-16 w-16 overflow-hidden rounded-md border">
                  <img src={url} alt={`Photo ${i + 1}`} className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setImageUrls((prev) => prev.filter((u) => u !== url))}
                    className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white"
                    aria-label="Remove photo"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {imageUrls.length < 6 && (
                <button
                  type="button"
                  onClick={() => galleryInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="flex h-16 w-16 flex-col items-center justify-center gap-0.5 rounded-md border border-dashed text-muted-foreground disabled:opacity-50"
                  aria-label="Add photo"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <ImageIcon className="h-5 w-5" />
                      <span className="text-[9px]">Add</span>
                    </>
                  )}
                </button>
              )}
            </div>
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) addCoverPhoto(file);
                e.target.value = "";
              }}
            />
          </div>

          <div className="flex gap-2 pt-1">
            <Button variant="ghost" onClick={onCancel} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!name.trim()) {
                  toast({ title: "Add a name", variant: "destructive" });
                  return;
                }
                setPhase("fill");
              }}
              className="flex-1"
            >
              Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (phase === "fill") {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="space-y-4 pt-6">
          <div className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Add the weights</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Pick any way to fill the list. You'll review and edit every weight before publishing.
          </p>

          {extracting ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm font-medium">Reading the weights…</p>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  className="flex w-full items-center gap-3 rounded-lg border-2 border-dashed border-primary/30 bg-primary/5 p-4 text-left text-primary transition hover:border-primary/50"
                >
                  <Camera className="h-6 w-6 shrink-0" />
                  <span className="text-sm font-medium">Take a photo of your weight sheet</span>
                </button>
                <button
                  type="button"
                  onClick={() => galleryInputRef.current?.click()}
                  className="flex w-full items-center gap-3 rounded-lg border-2 border-dashed border-primary/30 bg-primary/5 p-4 text-left text-primary transition hover:border-primary/50"
                >
                  <ImageIcon className="h-6 w-6 shrink-0" />
                  <span className="text-sm font-medium">Choose a photo from your gallery</span>
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex w-full items-center gap-3 rounded-lg border-2 border-dashed border-primary/30 bg-primary/5 p-4 text-left text-primary transition hover:border-primary/50"
                >
                  <FileUp className="h-6 w-6 shrink-0" />
                  <span className="text-sm font-medium">Upload a spreadsheet (.xlsx or .csv)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPhase("grid")}
                  className="flex w-full items-center gap-3 rounded-lg border-2 border-dashed border-border p-4 text-left text-foreground transition hover:border-primary/50"
                >
                  <Pencil className="h-6 w-6 shrink-0" />
                  <span className="text-sm font-medium">Enter weights manually</span>
                </button>
              </div>
            </>
          )}

          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            {...(isNativeCapture ? { capture: "environment" as const } : {})}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) extractImage(file);
              e.target.value = "";
            }}
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) extractImage(file);
              e.target.value = "";
            }}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv,.txt,.tsv,text/csv,text/plain,image/*,application/pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileUpload(file);
              e.target.value = "";
            }}
          />

          {initial ? (
            <Button variant="ghost" onClick={onCancel} className="w-full">
              Cancel
            </Button>
          ) : (
            <Button variant="ghost" onClick={() => setPhase("basics")} className="w-full">
              <ChevronLeft className="mr-1 h-4 w-4" />
              Back
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  if (phase === "grid") {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="space-y-3 pt-6">
          <div className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">{name || "Weight batch"}</h2>
          </div>

          <div className="flex items-end gap-2 rounded-lg border bg-muted/40 p-2.5">
            <div className="flex-1 space-y-1">
              <Label className="text-[11px]">Price per lb ($)</Label>
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={rateInput}
                onChange={(e) => setRateInput(e.target.value)}
                placeholder="Type price per lb"
                className="h-9"
              />
            </div>
            <p className="pb-2 text-[11px] text-muted-foreground">
              Edit once, all prices recompute
            </p>
          </div>

          <div className="overflow-hidden rounded-lg border">
            <div className="grid grid-cols-[28px_1fr_1fr_32px] items-center gap-1 border-b bg-muted/60 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span>#</span>
              <span>Weight (lb)</span>
              <span>Price ($)</span>
              <span />
            </div>
            <div className="max-h-[40vh] overflow-y-auto">
              {rows.map((row, i) => {
                const centi = centiWeights[i];
                const flag = flags[i] ?? row.flag;
                const autoCents = centi != null && pricePerLbCents > 0 ? priceForUnit(centi, pricePerLbCents) : 0;
                const priceValue =
                  row.priceOverride != null
                    ? row.priceOverride
                    : centi != null && pricePerLbCents > 0
                      ? dollars(autoCents)
                      : "";
                return (
                  <div
                    key={row.id}
                    className={`grid grid-cols-[28px_1fr_1fr_32px] items-center gap-1 border-b px-2 py-1 last:border-b-0 ${
                      flag ? "bg-amber-50" : ""
                    }`}
                  >
                    <span className="text-[11px] tabular-nums text-muted-foreground">{i + 1}</span>
                    <div className="relative">
                      <Input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        value={row.weight}
                        onChange={(e) => updateRow(row.id, { weight: e.target.value })}
                        placeholder="0.00"
                        className={`h-8 text-sm ${flag ? "border-amber-400" : ""}`}
                      />
                      {flag && (
                        <AlertTriangle className="pointer-events-none absolute right-1.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-amber-500" />
                      )}
                    </div>
                    <Input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      value={priceValue}
                      onChange={(e) => updateRow(row.id, { priceOverride: e.target.value })}
                      placeholder={pricePerLbCents > 0 ? "auto" : "0.00"}
                      className={`h-8 text-sm ${row.priceOverride != null ? "font-semibold text-primary" : ""}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeRow(row.id)}
                      aria-label="Delete row"
                      className="flex h-8 w-8 items-center justify-center text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {flags.some(Boolean) && (
            <p className="flex items-center gap-1.5 text-[11px] text-amber-700">
              <AlertTriangle className="h-3.5 w-3.5" />
              Highlighted weights look off from the rest — check them.
            </p>
          )}

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={addRow} className="flex-1">
              <Plus className="mr-1 h-4 w-4" />
              Add row
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPhase("fill")} className="flex-1">
              <Camera className="mr-1 h-4 w-4" />
              Add from photo / file
            </Button>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2 text-xs">
            <span>
              <span className="font-semibold">{totals.count}</span> units
            </span>
            <span>
              <span className="font-semibold">{formatWeight(totals.totalCenti)}</span> total
            </span>
            <span>
              <span className="font-semibold">${dollars(totals.totalCents)}</span> total
            </span>
          </div>

          <div className="flex gap-2 pt-1">
            <Button variant="ghost" onClick={() => setPhase("basics")} className="flex-1">
              <ChevronLeft className="mr-1 h-4 w-4" />
              Back
            </Button>
            <Button onClick={goToTiers} className="flex-1">
              Set sizes
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // phase === "tiers"
  const maxTiers = Math.min(5, validCentiWeights.length);
  return (
    <Card className="w-full max-w-md mx-auto">
      <CardContent className="space-y-4 pt-6">
        <div className="flex items-center gap-2">
          <Scale className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Group into sizes</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          We split your {validCentiWeights.length} units into {tierCount} sizes. Drag the cut points to adjust.
        </p>

        <div className="flex items-center gap-2">
          <Label className="text-xs">Number of sizes</Label>
          <div className="flex gap-1">
            {Array.from({ length: Math.max(1, maxTiers - 1) }, (_, k) => k + 2).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => {
                  setTierCount(n);
                  const buckets = bucketIntoTiers(validCentiWeights, n);
                  setCutPoints(buckets.slice(0, -1).map((b) => b.max));
                }}
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition ${
                  tierCount === n
                    ? "bg-primary text-primary-foreground"
                    : "border border-border hover:border-primary/50"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        {cutPoints.length > 0 && weightCeil > weightFloor && (
          <div className="space-y-3 rounded-lg border p-3">
            <Label className="text-xs">Cut points (lb)</Label>
            {cutPoints.map((cut, i) => {
              const lower = i === 0 ? weightFloor : cutPoints[i - 1];
              const upper = i === cutPoints.length - 1 ? weightCeil : cutPoints[i + 1];
              return (
                <div key={i} className="space-y-1">
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>{tierPreview[i]?.label} ↔ {tierPreview[i + 1]?.label}</span>
                    <span className="font-semibold tabular-nums">{formatWeight(cut)}</span>
                  </div>
                  <Slider
                    min={weightFloor}
                    max={weightCeil}
                    step={5}
                    value={[cut]}
                    onValueChange={([v]) =>
                      setCutPoints((prev) =>
                        prev.map((c, k) =>
                          k === i ? Math.min(Math.max(v, lower), upper) : c,
                        ),
                      )
                    }
                  />
                </div>
              );
            })}
          </div>
        )}

        <div className="space-y-2">
          {tierPreview.map((t) => (
            <div key={t.tier} className="rounded-lg border p-2.5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">{t.label}</span>
                <span className="text-[11px] text-muted-foreground">
                  {t.count} unit{t.count === 1 ? "" : "s"}
                </span>
              </div>
              {t.count > 0 ? (
                <div className="mt-0.5 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>
                    {t.min != null && t.max != null
                      ? `${formatWeight(t.min)} – ${formatWeight(t.max)}`
                      : "—"}
                  </span>
                  <span>
                    {t.priceMin != null && t.priceMax != null
                      ? t.priceMin === t.priceMax
                        ? `$${dollars(t.priceMin)}`
                        : `$${dollars(t.priceMin)} – $${dollars(t.priceMax)}`
                      : "—"}
                  </span>
                </div>
              ) : (
                <p className="mt-0.5 text-[11px] text-amber-600">No units in this size</p>
              )}
              {t.count > 0 && (
                <div className="mt-1.5">
                  {tierImages[t.tier] ? (
                    <div className="flex items-center gap-2">
                      <img src={tierImages[t.tier]} alt="" className="h-9 w-9 rounded object-cover" />
                      <button
                        type="button"
                        onClick={() =>
                          setTierImages((prev) => {
                            const next = { ...prev };
                            delete next[t.tier];
                            return next;
                          })
                        }
                        className="text-[11px] text-muted-foreground underline"
                      >
                        Remove photo
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-x-2 whitespace-nowrap text-[11px]">
                      <span className="shrink-0 text-muted-foreground">Photo:</span>
                      {uploadingTier === t.tier ? (
                        <span className="text-muted-foreground">Uploading…</span>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => pickTierPhoto(t.tier, "camera")}
                            className="flex shrink-0 items-center gap-1 font-medium text-primary underline"
                          >
                            <Camera className="h-3 w-3" /> Camera
                          </button>
                          <button
                            type="button"
                            onClick={() => pickTierPhoto(t.tier, "gallery")}
                            className="flex shrink-0 items-center gap-1 font-medium text-primary underline"
                          >
                            <ImageIcon className="h-3 w-3" /> Gallery
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          <input
            ref={tierPhotoInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onTierPhotoChange}
          />
          <input
            ref={tierCameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={onTierPhotoChange}
          />
        </div>

        <div className="flex gap-2 pt-1">
          <Button variant="ghost" onClick={() => setPhase("grid")} className="flex-1">
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
          <Button
            onClick={() => publish.mutate()}
            disabled={publish.isPending || totals.count === 0}
            className="flex-1"
          >
            {publish.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-2 h-4 w-4" />
            )}
            Publish {totals.count} units
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
